import React from 'react';

import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as NavConstants from 'constants/NavConstants';
import {SearchSection} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ConnectionSchema} from 'instruments/connection-schemas/ConnectionSchemaConstants';
import {CrumbLink, SplitBreadcrumbs} from 'nav/Breadcrumbs';
import ItemListing from 'nav/sidepanel/listing/ItemListing';
import {getNavigationURL} from 'nav/sidepanel/listing/SearchURLs';
import {getSelectedPageSize} from 'nav/sidepanel/PageSizeUtil';
import {SavedSearch} from 'search/saved-searches/SavedSearchUtil';
import {Filter, FilterKey, keyToEmptyFilter, SearchParams, SearchType, SortKey} from 'search/SearchConstants';
import {
  getAppliedFiltersFromUserPreference,
  getAppliedSortsFromUserPreference,
} from 'search/SearchPreferences';
import * as SearchUtil from 'search/SearchUtil';
import withSearching, {NavigationUpdate, SearchProps} from 'search/withSearching';

import NewConnectionDropdown from './NewConnectionDropdown';

interface SidepanelProps {
  activeItemId: string | null;
  isCompactDisplay: boolean;
  setIsCompactDisplay: (isCompact: boolean) => void;
  isExpanded: boolean;
  connectionsTab: JSX.Element;
}

type ConnectionSidepanelListingProps = SidepanelProps & SearchProps;

export function ConnectionSidepanelListing(props: ConnectionSidepanelListingProps): JSX.Element {
  const connectionSchemas = (props.schemasByType[ModelTypeConstants.CONNECTION_SCHEMA] ??
    []) as Array<ConnectionSchema>;
  return (
    <div className="sidepanel">
      <ItemListing
        {...props}
        actionSource={ActionOrigin.CONNECTIONS_SIDEPANEL}
        savedSearchSection={SearchSection.CONNECTIONS}
        additionalHeaderButtons={<NewConnectionDropdown connectionSchemas={connectionSchemas} />}
        availableSortKeys={[SortKey.SORT_NAME_KEY, SortKey.SORT_MODIFIED_AT_KEY, SortKey.SORT_CREATED_AT_KEY]}
        availableTypes={[ModelTypeConstants.CONNECTION]}
        // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
        makeCrumbs={(settingsComponents?: JSX.Element) => (
          <ConnectionSidepanelCrumbs
            activeSavedSearch={props.activeSavedSearch}
            onSelectSavedSearch={props.onSelectSavedSearch}
            onSearch={props.onSearch}
            searchParams={props.searchParams}
            settingsComponents={settingsComponents}
          />
        )}
        extraPrimaryFilterOptions={[]}
        preservedFilterKeys={[]}
        section={NavConstants.Section.CONNECTIONS}
        sectionTabs={props.connectionsTab}
      />
    </div>
  );
}

function ConnectionSidepanelCrumbs(props: {
  activeSavedSearch?: SavedSearch;
  onSelectSavedSearch: (savedSearch?: SavedSearch) => Promise<void>;
  onSearch: (searchParams: SearchParams) => void;
  searchParams: SearchParams;
  settingsComponents?: JSX.Element;
}): JSX.Element {
  if (props.activeSavedSearch) {
    return (
      <SplitBreadcrumbs
        crumbs={[
          <CrumbLink
            key="connections"
            text="Connections"
            onClick={() => {
              props.onSearch(
                SearchUtil.clearSearchParams(props.searchParams, [], getDefaultConnectionFilters())
              );
              props.onSelectSavedSearch(undefined);
            }}
          />,
          <CrumbLink key="root" text={props.activeSavedSearch.name} />,
        ]}
        settingsIcon={props.settingsComponents}
      />
    );
  }

  return (
    <SplitBreadcrumbs
      crumbs={[<span key="root">Connections</span>]}
      settingsIcon={props.settingsComponents}
    />
  );
}

export function getConnectionsURL(
  prevProps: ConnectionSidepanelListingProps,
  props: ConnectionSidepanelListingProps
): NavigationUpdate | null {
  return props.isExpanded
    ? {
        url: getNavigationURL('/connections/listing', props.searchParams, props.filterErrors),
        options: {replace: prevProps.isExpanded === props.isExpanded},
      }
    : null;
}

function getDefaultConnectionFilters(): Array<Filter> {
  const filters = getAppliedFiltersFromUserPreference(NavConstants.Section.CONNECTIONS);
  if (!filters.find((filter) => filter.key === FilterKey.TYPES)) {
    filters.push({
      ...keyToEmptyFilter(FilterKey.TYPES),
      value: [ModelTypeConstants.CONNECTION],
    });
  }
  return filters;
}

export function getDefaultConnectionSearchParams(): SearchParams {
  return {
    filters: getDefaultConnectionFilters(),
    offset: 0,
    limit: getSelectedPageSize(),
    query: '',
    sorts: getAppliedSortsFromUserPreference(NavConstants.Section.CONNECTIONS),
  };
}

export default withSearching(
  ConnectionSidepanelListing,
  SearchType.CONNECTIONS,
  NavConstants.SECTIONS.CONNECTIONS,
  {defaultSearchParams: getDefaultConnectionSearchParams(), getNavigationUpdate: getConnectionsURL}
);
